// LogsList.tsx
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  InputRef,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import { TFilterModel } from "../../../types/config";
import {
  ePrivileges,
  eREPORTTYPE,
  eResultCode,
  reportTypeMapping,
} from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { dateFormat, defaultFilterParams } from "../../../utils/constants";
import utils from "../../../utils";
import { TProductRate, TServicePartnerRates } from "../../../types";
import DateUtils from "../../../utils/date";
import ColumnManager from "../../../components/configurable-columns";
import {
  CloseOutlined,
  EditOutlined,
  FilterFilled,
  FilterOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import Highlighter from "react-highlight-words";
import { FilterDropdownProps } from "antd/es/table/interface";
import Utils from "../../../utils";
import CustomDrawer from "../../../components/drawer";
import { UserContext } from "../../../context";
import {
  GetCustomerAMCRateList,
  GetPartnerRateList,
} from "../../../utils/api.constant";
import PartnerProductRateForm from "./partner-rate-form";
import GenericPopover from "../../../components/pop-over";
import ActionCard from "../../../components/actionCard/actionCard";

export default function PartnerRateList() {
  // initialization of hooks
  const { post } = useFetch(); // custom hook used to call the apis
  const [isLoading, setIsLoading] = useState(true); // to manage the loader
  const [data, setData] = useState<TServicePartnerRates[]>([]); // to store config group data
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams); // object to fetch the data in list
  const { onShowToast } = useToast(); // custom hook used to show toast
  const searchInput = useRef<InputRef>(null);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const context: any = useContext(UserContext);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const handleReset = (clearFilters: FilterDropdownProps["clearFilters"]) => {
    if (clearFilters) {
      clearFilters();
    }
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: any): any => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e: any) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,.45)", marginRight: 4 }}
            />
          } // Adds and spaces the search icon
          value={selectedKeys[0]}
          onChange={(e: any) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px", // Ensure spacing for both icon and text
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: any
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const commonSorter = (a: any, b: any, key: string) => {
    const valueA = a[key] as string;
    const valueB = b[key] as string;
    return valueA?.localeCompare(valueB);
  };

  const initialColumns: any[] = [
    {
      title: "Service Partner",
      dataIndex: "servicePartnerName",
      key: "servicePartnerName",
      filterSearch: true,
      width: "15%",
      sorter: (a: TServicePartnerRates, b: TServicePartnerRates) =>
        String(a.servicePartnerName).localeCompare(
          String(b.servicePartnerName)
        ),
      // filters: utils.getUniqueFilters(data, "servicePartnerName"),
      onFilter: (
        value: string | number | boolean,
        record: TServicePartnerRates
      ) => record.servicePartnerName?.includes(value as string),
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      width: "15%",
      filterSearch: true,
      sorter: (a: TServicePartnerRates, b: TServicePartnerRates) =>
        String(a.productName).localeCompare(String(b.productName)),
      // filters: utils.getUniqueFilters(data, "productName"),
      onFilter: (
        value: string | number | boolean,
        record: TServicePartnerRates
      ) => record.productName?.includes(value as string),
    },
    {
      title: "Product Code",
      dataIndex: "productCode",
      key: "productCode",
      width: "10%",
      filterSearch: true,
      sorter: (a: TServicePartnerRates, b: TServicePartnerRates) =>
        String(a.productCode).localeCompare(String(b.productCode)),
      // filters: utils.getUniqueFilters(data, "productCode"),
      onFilter: (
        value: string | number | boolean,
        record: TServicePartnerRates
      ) => record.productCode?.includes(value as string),
    },
    {
      title: "Installation Rate",
      dataIndex: "installationRate",
      key: "installationRate",
      // ...getColumnSearchProps("installationRate"),
      width: "10%",
      // filterSearch: true,
      // sorter: (a: any, b: any) => commonSorter(a, b, "installationRate"),
      render: (rate: number) => utils.formatToIndianCurrency(rate),
    },
    {
      title: "AMC Rate(yearly)",
      dataIndex: "amcRateYearly",
      key: "amcRateYearly",
      // ...getColumnSearchProps("amcRateYearly"),
      width: "10%",
      // filterSearch: true,
      // sorter: (a: any, b: any) => commonSorter(a, b, "amcRateYearly"),
      render: (rate: number) => utils.formatToIndianCurrency(rate),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "4%",
      render: (record: TServicePartnerRates) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}

                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EIDTPRODUCTRATE
                ) ? (
                  <CustomDrawer
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={PartnerProductRateForm}
                    componentProps={{
                      id: record.id,
                      onRefreshList: () => fetchData(),
                      partnerProductRate: record,
                    }}
                    title="Product Rate Form"
                    size="default"
                  />
                ) : null}
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns);

  useEffect(() => {
    fetchData();
  }, []);

  const updateColumnFilters = (dataset: TServicePartnerRates[]) => {
    const servicePartnerFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.servicePartnerName)
          .map((item) => item.servicePartnerName)
      )
    ).map((name) => ({ text: name, value: name }));

    const productFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.productName)
          .map((item) => item.productName)
      )
    ).map((product) => ({ text: product, value: product }));

    const productCodeFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.productCode)
          .map((item) => item.productCode)
      )
    ).map((code) => ({ text: code, value: code }));

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "servicePartnerName") {
          return { ...col, filters: servicePartnerFilters };
        }
        if (col.key === "productName") {
          return { ...col, filters: productFilters };
        }
        if (col.key === "productCode") {
          return { ...col, filters: productCodeFilters };
        }
        return col;
      })
    );
  };

  const filteredData = data.filter((item) => {
    // Search filter
    const searchMatch =
      searchText === "" ||
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(searchText.toLowerCase())
      );

    if (!searchMatch) return false;

    // Column filters
    const servicePartnerMatch =
      !activeFilters.servicePartnerName ||
      !activeFilters.servicePartnerName.length ||
      (item.servicePartnerName &&
        activeFilters.servicePartnerName.includes(item.servicePartnerName));

    const productNameMatch =
      !activeFilters.productName ||
      !activeFilters.productName.length ||
      (item.productName &&
        activeFilters.productName.includes(item.productName));

    const productCodeMatch =
      !activeFilters.productCode ||
      !activeFilters.productCode.length ||
      (item.productCode &&
        activeFilters.productCode.includes(item.productCode));

    return (
      searchMatch && servicePartnerMatch && productNameMatch && productCodeMatch
    );
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetPartnerRateList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map(
          (item: TServicePartnerRates, index: number) => ({
            key: (item.id ?? "").toString(),
            srNo: index + 1,
            ...item,
          })
        );
        setData(formattedData);
        setFilterParams({
          ...(filterModel as TFilterModel),
          totalRows: data.length,
        });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        // icon={<FaObjectGroup />}
        title={`${filterParams?.totalRows} Total Product Rate`}
        size="large"
      >
        <Space>
          {/* {Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADSLAREPORT
          ) ||
          Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADSERVICEINSTALLATIONREPORT
          ) ||
          Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADYEARLYDEALERREPORT
          ) ||
          Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADDEALERACTIVEASSETServicePartnerRates
          ) ? ( */}

          {Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.ADDPRODUCTRATE
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={PartnerProductRateForm}
              componentProps={{
                id: 0,
                onRefreshList: () => fetchData(),
              }}
              title="PartnerRateForm"
              size="default"
            />
          ) : null}

          {/* ) : null} */}

          {/* MOVED TO REPORT LIST */}
          {/* <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Schedule
              </Button>
            }
            component={ScheduleReportForm} // Passing DealerForm component
            title="Schedule Report"
            size="default"
          /> */}
          <Input
            placeholder="Filter by keywords"
            value={globalsearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`service_partner_product_rate_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>
      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
